import React, { useEffect, useState } from "react";
import { supabase } from "../../integrations/supabase/client";
import { useSupabaseAuth } from "../../context/SupabaseAuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import EcoTips from "./EcoTips";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import GroqTTSService from "../../services/groqTtsService";

interface CarbonGoal {
  id: string;
  period: "monthly" | "weekly";
  period_start: string;
  goal_kg: number;
}

interface CarbonLog {
  date: string;
  total_co2: number;
}

const getPeriodStart = (period: "monthly" | "weekly", date: Date) => {
  if (period === "monthly") {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  } else {
    // Start of week (Monday)
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }
};

const ProgressView: React.FC<{ refreshTrigger?: number; month?: Date }> = ({
  refreshTrigger,
  month,
}) => {
  const { user } = useSupabaseAuth();
  const [goal, setGoal] = useState<CarbonGoal | null>(null);
  const [logs, setLogs] = useState<CarbonLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [period, setPeriod] = useState<"monthly" | "weekly">("monthly");
  const [monthlyTrends, setMonthlyTrends] = useState<
    { month: string; total: number }[]
  >([]);
  const [showEcoTips, setShowEcoTips] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null,
  );

  // Ref for the component
  const progressRef = React.useRef<HTMLDivElement>(null);

  // Listen for external trigger events
  useEffect(() => {
    const handleTriggerSummary = () => {
      handleSummarizeAndSpeak();
    };

    const handleTriggerGoalEdit = () => {
      handleOpenGoalModal();
    };

    const element = progressRef.current;
    if (element) {
      element.addEventListener("triggerSummary", handleTriggerSummary);
      element.addEventListener("triggerGoalEdit", handleTriggerGoalEdit);
      return () => {
        element.removeEventListener("triggerSummary", handleTriggerSummary);
        element.removeEventListener("triggerGoalEdit", handleTriggerGoalEdit);
      };
    }
  }, []);

  // Fetch goal and logs
  useEffect(() => {
    const fetchGoalAndLogs = async () => {
      setLoading(true);
      if (!user) return;
      const today = month ? new Date(month) : new Date();
      const periodStart = getPeriodStart(period, today)
        .toISOString()
        .slice(0, 10);
      // Fetch goal
      const { data: goals } = await supabase
        .from("carbon_goals")
        .select("*")
        .eq("user_id", user.id)
        .eq("period", period)
        .eq("period_start", periodStart)
        .limit(1);
      setGoal(goals && goals.length > 0 ? goals[0] : null);
      // Fetch logs for current period
      const { data: logsData } = await supabase
        .from("carbon_logs")
        .select("date,total_co2")
        .eq("user_id", user.id)
        .gte("date", periodStart)
        .lte("date", today.toISOString().slice(0, 10));
      setLogs(logsData || []);
      // Fetch logs for last 12 months for trend
      const start12mo = new Date(today.getFullYear(), today.getMonth() - 11, 1);
      const { data: trendLogs } = await supabase
        .from("carbon_logs")
        .select("date,total_co2")
        .eq("user_id", user.id)
        .gte("date", start12mo.toISOString().slice(0, 10))
        .lte("date", today.toISOString().slice(0, 10));
      // Aggregate by month
      const monthMap: { [key: string]: number } = {};
      (trendLogs || []).forEach((l) => {
        const d = new Date(l.date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        monthMap[key] = (monthMap[key] || 0) + (l.total_co2 || 0);
      });
      const months: { month: string; total: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        // Only include months from the current year
        if (d.getFullYear() === today.getFullYear()) {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          months.push({
            month: d.toLocaleString("default", { month: "short" }),
            total: Math.round((monthMap[key] || 0) * 10) / 10,
          });
        }
      }
      setMonthlyTrends(months);
      setLoading(false);
    };
    fetchGoalAndLogs();
  }, [period, showGoalModal, refreshTrigger, month]); // react to month changes

  // Calculate progress
  const total = logs.reduce((sum, l) => sum + (l.total_co2 || 0), 0);
  const goalValue = goal?.goal_kg || 0;
  const percent = goalValue > 0 ? Math.min(100, (total / goalValue) * 100) : 0;
  const onTrack = goalValue > 0 && total <= goalValue;

  // Calculate day streak (consecutive days with a log, up to today)
  function calculateStreak(logs: CarbonLog[]): number {
    if (!logs.length) return 0;
    // Get unique log dates as YYYY-MM-DD strings
    const logDates = Array.from(new Set(logs.map((l) => l.date)))
      .sort()
      .reverse();
    let streak = 0;
    let current = new Date();
    for (let i = 0; i < logDates.length; i++) {
      const logDate = new Date(logDates[i]);
      // Compare only the date part
      if (
        logDate.getFullYear() === current.getFullYear() &&
        logDate.getMonth() === current.getMonth() &&
        logDate.getDate() === current.getDate()
      ) {
        streak++;
        // Move to previous day
        current.setDate(current.getDate() - 1);
      } else {
        break;
      }
    }
    return streak;
  }
  const streak = calculateStreak(logs);

  // Calculate improvement vs last period
  const [prevTotal, setPrevTotal] = useState<number | null>(null);
  useEffect(() => {
    const fetchPrevPeriod = async () => {
      const user = supabase.auth.getUser
        ? (await supabase.auth.getUser()).data.user
        : null;
      if (!user) return;
      const today = month ? new Date(month) : new Date();
      let prevStart: Date, prevEnd: Date;
      if (period === "monthly") {
        prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        prevEnd = new Date(today.getFullYear(), today.getMonth(), 0); // last day of previous month
      } else {
        // Weekly: previous week (Mon-Sun)
        const now = new Date();
        const day = now.getDay();
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() - day - 6);
        prevStart = new Date(lastMonday);
        prevEnd = new Date(lastMonday);
        prevEnd.setDate(prevStart.getDate() + 6);
      }
      const { data: prevLogs } = await supabase
        .from("carbon_logs")
        .select("date,total_co2")
        .eq("user_id", user.id)
        .gte("date", prevStart.toISOString().slice(0, 10))
        .lte("date", prevEnd.toISOString().slice(0, 10));
      const prevSum = (prevLogs || []).reduce(
        (sum, l) => sum + (l.total_co2 || 0),
        0,
      );
      setPrevTotal(prevSum);
    };
    fetchPrevPeriod();
  }, [period, showGoalModal, month]);

  // Cleanup audio URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (currentAudio) {
        currentAudio.pause();
      }
    };
  }, []); // Only run on unmount

  let improvement = 0;
  if (prevTotal !== null && prevTotal > 0) {
    improvement = Math.round(((prevTotal - total) / prevTotal) * 100);
  }

  // Handlers
  const handleOpenGoalModal = () => {
    setGoalInput(goal?.goal_kg?.toString() || "");
    setShowGoalModal(true);
  };
  const handleSaveGoal = async () => {
    if (!user) return;
    const today = new Date();
    const periodStart = getPeriodStart(period, today)
      .toISOString()
      .slice(0, 10);
    if (goal) {
      await supabase
        .from("carbon_goals")
        .update({ goal_kg: Number(goalInput) })
        .eq("id", goal.id);
    } else {
      await supabase.from("carbon_goals").insert({
        user_id: user.id,
        period,
        period_start: periodStart,
        goal_kg: Number(goalInput),
      });
    }
    setShowGoalModal(false);
  };

  const handleSummarizeAndSpeak = async () => {
    setSummarizing(true);
    setSummary(null);
    setAudioUrl(null);
    setAudioBlob(null);
    setError(null);
    setIsPlaying(false);

    // Stop any currently playing audio
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }

    // Clean up previous audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    try {
      if (!user) throw new Error("User not authenticated");

      // Get structured carbon tracking data for the current month
      const carbonData = await fetchStructuredCarbonData();
      if (!carbonData)
        throw new Error("No carbon tracking data available for summary.");

      // Get Groq API key from environment
      const groqApiKey = import.meta.env.VITE_GROQ_API_KEY;
      if (!groqApiKey) {
        throw new Error(
          "Groq API key not configured. Please check your environment variables.",
        );
      }

      // Create Groq TTS service instance
      const service = new GroqTTSService({ apiKey: groqApiKey });

      // Create meaningful text summary from structured data
      const meaningfulSummary = createCarbonDataSummary(carbonData);

      // Use Groq to enhance and generate audio
      const audioResponse = await service.generateSmartAudio(
        meaningfulSummary,
        "Monthly Carbon Tracking Summary",
        "educational",
      );

      if (!audioResponse.audioBlob) {
        throw new Error(
          "Failed to generate audio. Please check your Groq API key.",
        );
      }

      // Store the blob to prevent garbage collection
      setAudioBlob(audioResponse.audioBlob);

      // Clean up previous audio URL if it exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      // Create a URL for the audio blob
      const newAudioUrl = URL.createObjectURL(audioResponse.audioBlob);
      setAudioUrl(newAudioUrl);

      // Use the enhanced text from Groq as summary
      setSummary(audioResponse.enhancedText || meaningfulSummary);
    } catch (err: any) {
      console.error("Error in handleSummarizeAndSpeak:", err);
      setError(
        err.message || "An error occurred while generating audio summary.",
      );
    } finally {
      setSummarizing(false);
    }
  };

  // Helper function to fetch structured carbon tracking data
  const fetchStructuredCarbonData = async () => {
    if (!user) return null;

    const today = month ? new Date(month) : new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    try {
      // Fetch current month's carbon logs with detailed breakdown
      const { data: monthlyLogs, error: logsError } = await supabase
        .from("carbon_logs")
        .select("*")
        .eq("user_id", user.id)
        .gte("date", currentMonth.toISOString().slice(0, 10))
        .lt("date", nextMonth.toISOString().slice(0, 10))
        .order("date", { ascending: true });

      if (logsError) throw logsError;

      // Fetch current goal
      const periodStart = getPeriodStart(period, today)
        .toISOString()
        .slice(0, 10);
      const { data: goalData, error: goalError } = await supabase
        .from("carbon_goals")
        .select("*")
        .eq("user_id", user.id)
        .eq("period", period)
        .eq("period_start", periodStart)
        .limit(1);

      // Calculate previous month for comparison
      const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const { data: prevMonthLogs, error: prevError } = await supabase
        .from("carbon_logs")
        .select("total_co2")
        .eq("user_id", user.id)
        .gte("date", prevMonth.toISOString().slice(0, 10))
        .lt("date", currentMonth.toISOString().slice(0, 10));

      // Aggregate data
      const monthlyData = monthlyLogs || [];
      let totalEmissions = 0;
      let foodEmissions = 0;
      let transportEmissions = 0;
      let utilitiesEmissions = 0;
      let goodsEmissions = 0;
      let loggedDays = new Set();

      monthlyData.forEach((log) => {
        totalEmissions += log.total_co2 || 0;
        loggedDays.add(log.date);

        // Calculate category emissions (simplified calculation)
        if (log.food) {
          const foodArray = Array.isArray(log.food) ? log.food : [];
          foodEmissions += foodArray.reduce((sum: number, f: any) => {
            const servings = parseFloat(f.servings) || 0;
            const factor =
              f.type === "meat" ? 5 : f.type === "vegetarian" ? 2 : 1.5;
            return sum + servings * factor;
          }, 0);
        }

        if (log.transport) {
          const transportArray = Array.isArray(log.transport)
            ? log.transport
            : [];
          transportEmissions += transportArray.reduce((sum: number, t: any) => {
            const distance = parseFloat(t.distance) || 0;
            const factor =
              t.mode === "car"
                ? 0.21
                : t.mode === "bus"
                  ? 0.09
                  : t.mode === "flight"
                    ? 0.255
                    : 0;
            return sum + distance * factor;
          }, 0);
        }

        if (log.home) {
          const electricity = parseFloat(log.home.electricity) || 0;
          const gas = parseFloat(log.home.gas) || 0;
          const water = parseFloat(log.home.water) || 0;
          utilitiesEmissions += electricity * 0.4 + gas * 0.2 + water * 0.0003;
        }

        if (log.goods != null && log.goods !== "") {
          const goodsValue = parseFloat(log.goods) || 0;
          goodsEmissions += goodsValue * 0.5;
        }
      });

      const prevMonthTotal = (prevMonthLogs || []).reduce(
        (sum, log) => sum + (log.total_co2 || 0),
        0,
      );
      const currentGoal =
        goalData && goalData.length > 0 ? goalData[0].goal_kg : null;

      return {
        totalEmissions: Math.round(totalEmissions * 10) / 10,
        categoryBreakdown: {
          food: Math.round(foodEmissions * 10) / 10,
          transport: Math.round(transportEmissions * 10) / 10,
          utilities: Math.round(utilitiesEmissions * 10) / 10,
          goods: Math.round(goodsEmissions * 10) / 10,
        },
        loggedDaysCount: loggedDays.size,
        currentMonthDays: new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
        ).getDate(),
        currentGoal,
        previousMonthTotal: Math.round(prevMonthTotal * 10) / 10,
        period,
        monthName: today.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
      };
    } catch (error) {
      console.error("Error fetching structured carbon data:", error);
      return null;
    }
  };

  // Helper function to create meaningful summary from carbon data
  const createCarbonDataSummary = (data: any): string => {
    const {
      totalEmissions,
      categoryBreakdown,
      loggedDaysCount,
      currentMonthDays,
      currentGoal,
      previousMonthTotal,
      monthName,
    } = data;

    const trackingProgress = Math.round(
      (loggedDaysCount / currentMonthDays) * 100,
    );
    const improvement =
      previousMonthTotal > 0
        ? Math.round(
            ((previousMonthTotal - totalEmissions) / previousMonthTotal) * 100,
          )
        : 0;

    const goalStatus = currentGoal
      ? totalEmissions <= currentGoal
        ? "on track to meet"
        : "exceeding"
      : "no active";

    // Find the highest emission category
    const categories = Object.entries(categoryBreakdown).sort(
      ([, a], [, b]) => (b as number) - (a as number),
    );
    const topCategory = categories[0];
    const categoryName =
      topCategory[0] === "transport"
        ? "transportation"
        : topCategory[0] === "utilities"
          ? "home utilities"
          : topCategory[0];

    let summary = `Here's your carbon footprint summary for ${monthName}. `;

    summary += `You've tracked ${loggedDaysCount} out of ${currentMonthDays} days this month, which is ${trackingProgress}% tracking consistency. `;

    summary += `Your total emissions are ${totalEmissions} kilograms of CO2 equivalent. `;

    if (currentGoal) {
      summary += `You're ${goalStatus} your goal of ${currentGoal} kilograms. `;
    }

    if (previousMonthTotal > 0) {
      if (improvement > 0) {
        summary += `Great news - you've reduced your emissions by ${improvement}% compared to last month. `;
      } else if (improvement < 0) {
        summary += `Your emissions increased by ${Math.abs(improvement)}% compared to last month. `;
      } else {
        summary += `Your emissions remained stable compared to last month. `;
      }
    }

    summary += `Your largest emission source is ${categoryName}, accounting for ${Math.round(((topCategory[1] as number) / totalEmissions) * 100)}% of your footprint. `;

    // Add category breakdown
    summary += `Breaking down your emissions: ${categoryBreakdown.food} kg from food, `;
    summary += `${categoryBreakdown.transport} kg from transportation, `;
    summary += `${categoryBreakdown.utilities} kg from utilities, `;
    summary += `and ${categoryBreakdown.goods} kg from goods and services. `;

    // Add actionable insights
    if (topCategory[0] === "transport") {
      summary += `Consider using public transport or cycling for short trips to reduce transportation emissions. `;
    } else if (topCategory[0] === "food") {
      summary += `Try incorporating more plant-based meals to lower your food-related emissions. `;
    } else if (topCategory[0] === "utilities") {
      summary += `Look into energy-efficient appliances or renewable energy options to cut utility emissions. `;
    }

    summary += `Keep tracking consistently to maintain awareness and continue your environmental progress.`;

    return summary;
  };

  const handlePlayAudio = () => {
    if (!audioUrl || !audioBlob) {
      setError("Audio not available. Please generate the summary first.");
      return;
    }

    // Stop any currently playing audio
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }

    try {
      const audio = new Audio(audioUrl);

      // Set up event listeners before attempting to load
      audio.oncanplaythrough = () => {
        console.log("Audio can play through");
      };

      audio.onloadeddata = () => {
        console.log("Audio data loaded successfully");
      };

      audio.onplay = () => setIsPlaying(true);
      audio.onpause = () => setIsPlaying(false);
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentAudio(null);
      };

      audio.onerror = (event) => {
        console.error("Audio error event:", event);
        const errorMsg =
          "Failed to play audio. The audio file may be corrupted or unsupported.";
        setError(errorMsg);
        setIsPlaying(false);
        setCurrentAudio(null);
      };

      // Ensure the audio element has the correct MIME type
      if (audioBlob.type) {
        audio.setAttribute("type", audioBlob.type);
      }

      setCurrentAudio(audio);

      // Attempt to load and play the audio
      audio.load(); // Explicitly load the audio
      audio.play().catch((err) => {
        console.error("Audio play error:", err);
        setError(
          "Failed to play audio. Please try again or regenerate the summary.",
        );
        setIsPlaying(false);
        setCurrentAudio(null);
      });
    } catch (err) {
      console.error("Error creating audio element:", err);
      setError("Failed to create audio player. Please try again.");
      setIsPlaying(false);
    }
  };

  const handleStopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleDownloadAudio = () => {
    if (audioUrl) {
      const link = document.createElement("a");
      link.href = audioUrl;
      link.download = `carbon-dashboard-summary-${new Date().toISOString().split("T")[0]}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div ref={progressRef} data-progress-view="true" className="space-y-6">
      {/* Primary Goal Display */}
      <div className="space-y-4">
        {/* Goal Value Display */}
        <div className="text-center space-y-2">
          <div className="text-sm font-medium text-muted-foreground">
            {period === "monthly" ? "Monthly" : "Weekly"} Goal
          </div>
          <div className="text-4xl font-bold text-foreground">
            {goalValue ? `${goalValue}` : "---"}
            <span className="text-lg font-normal text-muted-foreground ml-1">
              kg CO₂e
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            {total.toFixed(1)} kg used
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-3">
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                onTrack ? "bg-green-500" : "bg-red-500"
              }`}
              style={{ width: `${Math.min(percent, 100)}%` }}
            />
          </div>

          {/* Status and Streak */}
          <div className="flex items-center justify-between">
            <div
              className={`text-sm font-semibold ${
                onTrack ? "text-green-600" : "text-red-600"
              }`}
            >
              {onTrack ? "✓ On Track" : "⚠ Over Goal"}
            </div>
            {streak > 0 && (
              <div className="text-sm text-muted-foreground">
                🔥 {streak} day streak
              </div>
            )}
          </div>
        </div>

        {/* Improvement Badge */}
        {prevTotal !== null && improvement !== 0 && (
          <div className="flex justify-center">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                improvement > 0
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
              }`}
            >
              {improvement > 0 ? "↓" : "↑"} {Math.abs(improvement)}% vs last{" "}
              {period}
            </div>
          </div>
        )}
      </div>

      {/* Monthly Trends Chart - Simplified */}
      {monthlyTrends.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-muted-foreground">
            Monthly Trends
          </h5>
          <div className="h-48 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={monthlyTrends}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="currentColor"
                  opacity={0.1}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "currentColor" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "currentColor" }}
                  width={35}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v: number) => [`${v} kg CO₂e`, "Emissions"]}
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#40e0d0"
                  strokeWidth={2}
                  dot={{ fill: "#40e0d0", r: 3 }}
                  activeDot={{
                    r: 5,
                    stroke: "#40e0d0",
                    strokeWidth: 2,
                    fill: "#fff",
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Goal Modal - Simplified */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                Set Goal
              </h3>

              <div className="space-y-3">
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-foreground"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="e.g. 150"
                  min={1}
                />

                <div className="grid grid-cols-2 gap-2">
                  <button
                    className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      period === "monthly"
                        ? "bg-[#40e0d0] text-black"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                    onClick={() => setPeriod("monthly")}
                  >
                    Monthly
                  </button>
                  <button
                    className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      period === "weekly"
                        ? "bg-[#40e0d0] text-black"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                    onClick={() => setPeriod("weekly")}
                  >
                    Weekly
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  className="flex-1 py-2 px-4 bg-[#40e0d0] text-black rounded-md font-medium hover:bg-[#40e0d0]/90 transition-colors"
                  onClick={handleSaveGoal}
                >
                  Save Goal
                </button>
                <button
                  className="flex-1 py-2 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => setShowGoalModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Eco Tips Modal */}
      {showEcoTips && (
        <EcoTips open={showEcoTips} onClose={() => setShowEcoTips(false)} />
      )}

      {/* AI Summary Modal - Simplified */}
      {summary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#40e0d0]">
                  Progress Summary
                </h3>
                <button
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  onClick={() => setSummary(null)}
                  aria-label="Close"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <p className="text-sm text-foreground leading-relaxed">
                  {summary}
                </p>

                {audioUrl && (
                  <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                      <Button
                        onClick={isPlaying ? handleStopAudio : handlePlayAudio}
                        variant="outline"
                        size="sm"
                        className="border-[#40e0d0] text-[#40e0d0] hover:bg-[#40e0d0]/10 flex-1"
                      >
                        {isPlaying ? "Stop" : "Play"} Audio
                      </Button>
                      <Button
                        onClick={handleDownloadAudio}
                        variant="ghost"
                        size="sm"
                        className="px-3"
                        title="Download"
                      >
                        ↓
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressView;
